import React, { useState, useEffect, useMemo, useCallback, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styles from './Gallery.module.scss';

// Lazy load the GalleryItem component
const GalleryItem = React.lazy(() => import('./GalleryItem'));

// Loading fallback component
const ImageSkeleton = () => (
  <div className={`${styles.gallery__item} ${styles.gallery__item_skeleton}`}>
    <div className={styles.gallery__image_skeleton}></div>
  </div>
);

const Gallery = () => {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Fisher-Yates shuffle algorithm - memoized to prevent recreation on every render
  const shuffleArray = useCallback((array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }, []);

  // Memoize categories array to prevent recreation on every render
  const categories = useMemo(() => ['all', 'pixel', 'anime', 'fantasy'], []);

  useEffect(() => {
    const loadGalleryConfig = async () => {
      try {
        const response = await fetch('/gallery/gallery.json');
        const data = await response.json();
        setImages(data.images);
      } catch (error) {
        console.error('Error loading gallery config:', error);
        setImages([]);
      }
    };

    loadGalleryConfig();
  }, []);

  // Memoize filtered images to prevent recalculation on every render
  const filteredImages = useMemo(() => {
    if (selectedCategory === 'all') {
      return shuffleArray(images);
    }
    return images.filter(image => image.path.startsWith(selectedCategory + '/'));
  }, [images, selectedCategory, shuffleArray]);

  // Memoize category change handler
  const handleCategoryClick = useCallback((category) => {
    setSelectedCategory(category);
  }, []);

  if (images.length === 0) {
    return <div className={styles.gallery}>{t('gallery.loading')}</div>;
  }

  return (
    <div className={styles.gallery}>
      <div className={styles.gallery__categories}>
        {categories.map(category => (
          <button
            key={category}
            className={`${styles.gallery__category} ${selectedCategory === category ? styles['gallery__category--active'] : ''}`}
            onClick={() => handleCategoryClick(category)}
          >
            {t(`gallery.categories.${category}`)}
          </button>
        ))}
      </div>
      <div className={styles.gallery__grid}>
        {filteredImages.map((image, index) => (
          <Suspense key={image.path} fallback={<ImageSkeleton />}>
            <GalleryItem 
              image={image} 
              index={index} 
            />
          </Suspense>
        ))}
      </div>
    </div>
  );
};

Gallery.propTypes = {
  folderPath: PropTypes.string,
  onFullscreenChange: PropTypes.func
};

export default Gallery; 